import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { students as emtStudents } from '../data/students';
import { paramedicStudents } from '../data/paramedicStudents';
import ImportClassModal from '../components/ImportClassModal';

const ClassroomManagement = () => {
  const [allStudents, setAllStudents] = useState([]); // Default to empty
  const [displayedStudents, setDisplayedStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [classes, setClasses] = useState([
    { id: 1, name: 'EMT 101 BEX SUM 2025' },
    { id: 2, name: 'PARAMEDIC 201 FALL 2025' },
  ]);
  const [importedClasses, setImportedClasses] = useState({}); // Store imported class data

  // Load imported classes from localStorage on component mount
  useEffect(() => {
    const savedClasses = localStorage.getItem('importedClasses');
    if (savedClasses) {
      try {
        const parsedClasses = JSON.parse(savedClasses);
        setClasses(prev => {
          // Merge with existing hardcoded classes, avoiding duplicates
          const existingNames = prev.map(c => c.name);
          const newClasses = parsedClasses.filter(c => !existingNames.includes(c.name));
          return [...prev, ...newClasses];
        });

        // Also load the student data for imported classes
        const importedStudentsData = {};
        parsedClasses.forEach(cls => {
          importedStudentsData[cls.name] = cls.students;
        });
        setImportedClasses(importedStudentsData);
      } catch (error) {
        console.error('Error loading imported classes from localStorage:', error);
      }
    }
  }, []);

  // Note: The filter dropdowns for year, semester, etc., are for UI demonstration.
  // The filtering logic is not connected to them as the student data doesn't contain this information yet.
  const years = [2024, 2025, 2026, 2027];
  const semesters = ['Summer', 'Fall', 'Spring'];
  const classNumbers = [100, 101, 201, 224, 225, 227]; // Added 201
  const sections = ['BEW', 'RV', 'OR', 'GL', 'BEX'];

  useEffect(() => {
    if (!selectedClass) {
      setDisplayedStudents([]);
      return;
    }

    let studentsToDisplay = [...allStudents];

    // Search logic
    if (searchTerm) {
      studentsToDisplay = studentsToDisplay.filter(student =>
        student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sorting logic
    if (sortKey) {
      studentsToDisplay.sort((a, b) => {
        if (a[sortKey] < b[sortKey]) return -1;
        if (a[sortKey] > b[sortKey]) return 1;
        return 0;
      });
    }

    setDisplayedStudents(studentsToDisplay);
  }, [selectedClass, searchTerm, sortKey, allStudents]);

  const handleClassSelection = (className) => {
    if (selectedClass === className) {
      setSelectedClass(null); 
      setAllStudents([]); // Clear students when toggling off
    } else {
      setSelectedClass(className);
      // Load the correct student list based on the selected class
      if (className === 'EMT 101 BEX SUM 2025') {
        setAllStudents(emtStudents);
      } else if (className === 'PARAMEDIC 201 FALL 2025') {
        setAllStudents(paramedicStudents);
      } else if (className === 'All Classes') {
        setAllStudents([...emtStudents, ...paramedicStudents]);
      } else {
        // Check if it's an imported class
        const importedStudents = importedClasses[className];
        if (importedStudents) {
          setAllStudents(importedStudents);
        } else {
          setAllStudents([]);
        }
      }

      // Reset filters when changing class
      setSearchTerm('');
      setSortKey('');
    }
  };

  const handleImportSuccess = (importData) => {
    const { className, students } = importData;

    // Add the new class to the classes array
    const newClass = {
      id: Date.now(), // Simple ID generation
      name: className,
      students: students
    };

    setClasses(prev => {
      const updatedClasses = [...prev, newClass];
      // Save to localStorage for persistence across page reloads
      localStorage.setItem('importedClasses', JSON.stringify(updatedClasses));
      return updatedClasses;
    });

    // Store the imported students data for this class
    setImportedClasses(prev => ({
      ...prev,
      [className]: students
    }));

    // Show success message
    alert(`Successfully imported class "${className}" with ${students.length} students!`);

    // You could automatically select the newly imported class if desired
    // handleClassSelection(className);
  };

  const handleDeleteStudent = (studentId) => {
    if (window.confirm("Are you sure you want to delete this student?")) {
        setAllStudents(prev => prev.filter(s => s.id !== studentId));
    }
  }

  const handleEditStudent = (studentId) => {
    // For now, this is a placeholder.
    // In a real app, you would show a modal or an inline form to edit the student.
    alert(`Editing student ID: ${studentId}`);
  }

  const handleProvisionUsers = async () => {
    if (!selectedClass || displayedStudents.length === 0) {
        alert("Please select a class with students to provision.");
        return;
    }

    if (!window.confirm(`Are you sure you want to provision users for ${displayedStudents.length} student(s) in ${selectedClass}? This will create Firebase Authentication accounts.`)) {
        return;
    }

    setIsProvisioning(true);
    const functions = getFunctions();
    const provisionStudentUser = httpsCallable(functions, 'provisionStudentUser');

    let successCount = 0;
    let existCount = 0;
    let errorCount = 0;
    const errorDetails = [];

    for (const student of displayedStudents) {
        try {
            // In a real-world scenario, you would have a more secure way of generating passwords.
            // For this example, we'll use a simple, predictable password.
            const tempPassword = `password${student.id}`;
            const displayName = `${student.firstName} ${student.lastName}`;

            const result = await provisionStudentUser({ 
                email: student.email, 
                password: tempPassword, 
                displayName: displayName 
            });

            if (result.data.status === 'success') {
                console.log(`Successfully provisioned: ${student.email}`);
                successCount++;
            } else if (result.data.status === 'already_exists') {
                console.log(`User already exists: ${student.email}`);
                existCount++;
            } else {
                console.error(`Unknown response for ${student.email}:`, result.data);
                errorCount++;
                errorDetails.push(`${student.email}: ${result.data.message}`);
            }
        } catch (error) {
            console.error(`Failed to provision ${student.email}:`, error);
            errorCount++;
            errorDetails.push(`${student.email}: ${error.message}`);
        }
    }

    setIsProvisioning(false);

    let summary = `Provisioning complete.\n\n`;
    summary += `Successfully created: ${successCount}\n`;
    summary += `Already existed: ${existCount}\n`;
    summary += `Failed: ${errorCount}`;

    if (errorCount > 0) {
        summary += `\n\nErrors:\n${errorDetails.join('\n')}`;
    }

    alert(summary);
  };


  return (
    <div className="container mx-auto p-4">
              <ImportClassModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImportSuccess={handleImportSuccess}
        />
      <h1 className="text-2xl font-bold mb-4">Classroom Management</h1>
      <div className="mb-4 flex justify-between items-center">
        <div>
            <button 
              onClick={() => handleClassSelection('All Classes')}
              className={`font-bold py-2 px-4 rounded mr-2 transition-all duration-200 shadow-lg active:shadow-inner active:translate-y-1 ${
                selectedClass === 'All Classes' 
                ? 'bg-primary text-white' 
                : 'bg-gray-200 text-text hover:bg-gray-300'
              }`}>
              All Classes
            </button>
          {classes.map(cls => (
            <button 
              key={cls.id} 
              onClick={() => handleClassSelection(cls.name)}
              className={`font-bold py-2 px-4 rounded mr-2 transition-all duration-200 shadow-lg active:shadow-inner active:translate-y-1 ${
                selectedClass === cls.name 
                ? 'bg-primary text-white' 
                : 'bg-gray-200 text-text hover:bg-gray-300'
              }`}>
              {cls.name}
            </button>
          ))}
        </div>
        <button onClick={() => setIsImportModalOpen(true)} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow-lg active:shadow-inner active:translate-y-1 transition-all duration-200">
          Import New Class
        </button>
      </div>

      <div className={`student-list-container ${selectedClass ? 'open' : ''}`}>
        <div className="p-1">
          {/* Search, Sort, and Filter UI */}
          <div className="mb-4 flex flex-wrap items-center gap-4 bg-table p-4 rounded-xl shadow">
            <input 
                type="text" 
                placeholder="Search students..." 
                className="border rounded px-2 py-1" 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
            />
            <select 
                className="border rounded px-2 py-1" 
                value={sortKey}
                onChange={e => setSortKey(e.target.value)}
            >
              <option value="">Sort by...</option>
              <option value="lastName">Last Name</option>
              <option value="firstName">First Name</option>
              <option value="id">OrgDefinedID</option>
            </select>
            {/* These filters are currently for UI display only */}
            <select className="border rounded px-2 py-1">
              <option value="">Filter by Year...</option>
              {years.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
            <select className="border rounded px-2 py-1">
              <option value="">Filter by Semester...</option>
              {semesters.map(sem => <option key={sem} value={sem}>{sem}</option>)}
            </select>
            <select className="border rounded px-2 py-1">
              <option value="">Filter by Class...</option>
              {classNumbers.map(num => <option key={num} value={num}>{num}</option>)}
            </select>
            <select className="border rounded px-2 py-1">
              <option value="">Filter by Section...</option>
              {sections.map(sec => <option key={sec} value={sec}>{sec}</option>)}
            </select>
            <button 
                onClick={handleProvisionUsers} 
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 shadow-lg active:shadow-inner active:translate-y-1 transition-all duration-200"
                disabled={isProvisioning || displayedStudents.length === 0}
            >
                {isProvisioning ? 'Provisioning Users...' : `Provision ${displayedStudents.length} Users`}
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto border border-gray-200 rounded-lg shadow-inner">
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-primary/10 sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-4 border-b text-left">OrgDefinedID</th>
                    <th className="py-3 px-4 border-b text-left">Last Name</th>
                    <th className="py-3 px-4 border-b text-left">First Name</th>
                    <th className="py-3 px-4 border-b text-left">Email</th>
                    <th className="py-3 px-4 border-b text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-secondary/10 transition-colors duration-150">
                      <td className="py-2 px-4 border-b">{student.id}</td>
                      <td className="py-2 px-4 border-b">{student.lastName}</td>
                      <td className="py-2 px-4 border-b">{student.firstName}</td>
                      <td className="py-2 px-4 border-b">{student.email}</td>
                      <td className="py-2 px-4 border-b text-center">
                        <button onClick={() => handleEditStudent(student.id)} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded mr-2 shadow-lg active:shadow-inner active:translate-y-1 transition-all duration-200">Edit</button>
                        <button onClick={() => handleDeleteStudent(student.id)} className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded shadow-lg active:shadow-inner active:translate-y-1 transition-all duration-200">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassroomManagement;
